import { TitanPanel } from "@lib/titan2/dist/game/control/TitanPanel";
import { TitanSpine } from "@lib/titan2/dist/game/control/TitanSpine";
import { TitanSprite } from "@lib/titan2/dist/game/control/TitanSprite";
import { CommonGameEvent } from "@lib/titan2/dist/game/controller/event/CommonGameEvent";
import { DialogEvent } from "@lib/titan2/dist/game/manager/DialogManager";
import { Array2D } from "@lib/titan2/dist/util/collection/Array2D";
import { Dictionary } from "@lib/titan2/dist/util/collection/Dictionary";
import { Logger } from "@lib/titan2/dist/util/log/Logger";
import { Timer } from "@lib/titan2/dist/util/time/Timer";
import { DestroyUtil } from "@lib/titan2/dist/util/view/DestroyUtil";
import gsap from "gsap";
import { SlingoEvent } from "../../../controller/event/SlingoEvent";
import { SlingoStyle } from "../style/SlingoStyle";
import { AbstractGameView } from "./AbstractGameView";
/**
 * Ticket.ts
 */
export class Ticket extends AbstractGameView {
    createProperties(properties, portraitProperties) {
        super.createProperties(properties, portraitProperties);
        this.landscapeProperties = properties;
        this.portraitProperties = portraitProperties;
        this.uiProperties = this.uiProperties;
    }
    addEventListeners() {
        super.addEventListeners();
        this.registerGame(SlingoEvent.DAB_ACTIVE, this.handleDabActive);
        this.registerGame(SlingoEvent.DAB_SUCCESS, this.handleDabSuccess);
        this.registerGame(SlingoEvent.DAB_COMPLETE, this.handleDabComplete);
        this.registerGame(SlingoEvent.DAB_HIGHLIGHT, this.handleDabHighlight);
        this.registerGame(CommonGameEvent.PURCHASE_ENTRY, this.handlePurchaseEntry);
        this.registerGame(SlingoEvent.REEL_STRIKE, this.handleReelStop);
        if (this.factory.useFlowStateMachine?.()) {
            this.registerGame(SlingoEvent.SLINGO_TICKET_ANIMATION, this.handleSlingo);
        }
        else {
            this.registerGame(SlingoEvent.SLINGO, this.handleSlingo);
        }
        this.registerGame(SlingoEvent.AVAILABLE_WIN, this.handleAvailableWin);
        this.registerGame(SlingoEvent.AVAILABLE_WIN_CLEAR, this.handleAvailableWinClear);
        this.registerGame(CommonGameEvent.SPIN_REQUEST, this.handleSpinRequest);
        this.registerGame(CommonGameEvent.COLLECT_REQUEST, this.handleCollectRequest);
        this.registerGame(CommonGameEvent.STANDARD_SPIN_RECOVERY, this.handleStandardSpinRecovery);
        this.registerGame(CommonGameEvent.FREE_SPIN_RECOVERY, this.handleFreeSpinRecovery);
        this.registerGame(CommonGameEvent.PURCHASE_SPIN_RECOVERY, this.handlePurchaseSpinRecovery);
        this.registerGame(SlingoEvent.BIG_WIN_ANIMATION_PHASE_ONE, this.handleBigWinAnimationPhaseOne);
        this.registerGame(SlingoEvent.FULL_HOUSE_ANIMATION_PHASE_ONE, this.handleFullHouseAnimationPhaseOne);
        this.registerGame(SlingoEvent.FREE_SPIN_AWARD, this.handleFreeSpinAward);
        this.registerCore(DialogEvent.ACTIVE, this.handleDialogActive);
        this.registerGame(SlingoEvent.DAB_ALL_SYMBOLS, this.dabAllSymbols);
    }
    create() {
        super.create();
        this.createBackground();
        this.createSymbols();
        this.createBigWinTimer();
        Logger.addObjectRef(this, "Ticket");
    }
    createBackground() {
        if (this.landscapeProperties.backgroundProperties) {
            this.background = TitanSprite.createSprite(this, "background", this.landscapeProperties.backgroundProperties, this.portraitProperties?.backgroundProperties);
        }
        if (this.landscapeProperties.backgroundAnimationProperties) {
            this.backgroundAnimation = TitanSpine.createSpine(this, this.landscapeProperties.backgroundAnimationProperties, this.portraitProperties?.backgroundAnimationProperties);
            this.backgroundAnimation.visible = false;
        }
    }
    createSymbols() {
        this.symbolsByNumber = new Dictionary();
        this.symbolsPanel = TitanPanel.createPanel(this, "symbolsPanel", this.uiProperties.symbolPanelProperties);
        let ticket = this.model.getTicket();
        let rows = ticket.getNumberOfRows();
        let columns = ticket.getNumberOfColumns();
        this.symbols = new Array2D(rows, columns);
        for (let row = 0; row < rows; row++) {
            for (let column = 0; column < columns; column++) {
                let symbol = this.createSymbol();
                symbol.setTrackingId(`${SlingoStyle.TICKET_SYMBOL}_${column}_${row}`);
                this.symbolsPanel.addToPanel(symbol, false);
                this.symbols.setValue(row, column, symbol);
                let value = ticket.getCellAt(row, column);
                this.symbolsByNumber.setValue(value.getNumber(), symbol);
                symbol.name = row + "_" + column + "_val_" + value.getNumber();
            }
        }
        this.symbolsPanel.arrange();
    }
    createSymbol() {
        let symbol = this.factory.createTicketSymbol();
        symbol.onSelection(this.handleSymbolSelected, this);
        symbol.disable();
        return symbol;
    }
    createBigWinTimer() {
        this.bigWinFlashTimer = new Timer(this.uiProperties.bigWinFlashDuration);
        this.bigWinFlashTimer.onUpdate(this.handleBigWinTimerUpdate, this);
    }
    resetSymbols() {
        this.destroySymbols();
        this.createSymbols();
    }
    destroySymbols() {
        if (this.symbolsPanel) {
            this.symbolsPanel.removeAll();
        }
        if (this.symbolsByNumber) {
            this.symbolsByNumber.clear();
        }
        if (this.symbols) {
            let ticket = this.model.getTicket();
            let rows = ticket.getNumberOfRows();
            let columns = ticket.getNumberOfColumns();
            for (let row = 0; row < rows; row++) {
                for (let column = 0; column < columns; column++) {
                    let symbol = this.symbols.getValue(row, column);
                    gsap.killTweensOf(symbol);
                    symbol.destroy();
                }
            }
        }
        this.symbols = null;
        DestroyUtil.destroy(this.symbolsPanel);
        this.symbolsPanel = null;
    }
    updateSymbols() {
        this.symbolsByNumber = new Dictionary();
        let ticket = this.model.getTicket();
        let rows = ticket.getNumberOfRows();
        let columns = ticket.getNumberOfColumns();
        for (let row = 0; row < rows; row++) {
            for (let column = 0; column < columns; column++) {
                let symbol = this.symbols.getValue(row, column);
                let value = ticket.getCellAt(row, column);
                symbol.update(value);
                this.symbolsByNumber.setValue(value.getNumber(), symbol);
                if (value.isDabbed()) {
                    symbol.dab();
                }
            }
        }
    }
    handlePurchaseEntry() {
        this.resetSymbols();
        this.updateSymbols();
        this.hideAvailableWin();
    }
    handleStandardSpinRecovery() {
        this.handlePurchaseEntry();
        if (this.model.isExpressGame() && this.model.getGameProgress().getSpinsRemaining() === 0) {
            this.model.setDabbedAfterStandardSpins(true);
        }
    }
    handleFreeSpinRecovery() {
        this.handlePurchaseEntry();
    }
    handlePurchaseSpinRecovery() {
        this.handlePurchaseEntry();
        if (this.model.isExpressGame()) {
            this.model.setDabbedAfterStandardSpins(true);
        }
    }
    handleBonusSelectionRecovery() {
        this.handlePurchaseEntry();
    }
    handleSymbolSelected(symbol) {
        console.log("handleSymbolSelected", symbol.isEnabled());
        if (symbol.isEnabled()) {
            let cell = symbol.getData();
            let ticketDab = this.model.getDabData();
            let cellsToDab = ticketDab.getCellsToDab();
            if (cellsToDab.indexOf(cell) >= 0) {
                this.disableSymbols();
                symbol.showSymbolHighlight();
                ticketDab.setSelectedCell(cell);
                this.dispatchGame(SlingoEvent.DAB_REQUEST);
            }
        }
    }
    handleDabActive() {
        let ticketDab = this.model.getDabData();
        if (ticketDab.getHighlights()) {
            this.highlightNumberCells(ticketDab.getCellsToDab());
        }
        else {
            this.highlightSuperJokerCells(ticketDab.getSuperJokerCells());
            this.highlightJokerCells(ticketDab.getJokerCells());
        }
    }
    highlightNumberCells(cells) {
        this.highlightCells(cells);
    }
    highlightSuperJokerCells(cells) {
        this.highlightCells(cells, true);
    }
    highlightJokerCells(cells) {
        this.highlightCells(cells);
    }
    highlightCells(cells, superJoker = false) {
        for (let i = 0; i < cells.length; i++) {
            this.highlightCell(cells[i], true, superJoker);
        }
    }
    highlightCell(cell, animate, superJoker = false) {
        let symbol = this.symbolsByNumber.getValue(cell.getNumber());
        if (superJoker) {
            symbol.highlightSuperWild(animate);
        }
        else {
            symbol.highlight(animate);
        }
        symbol.enable();
    }
    handleDabSuccess() {
        let ticketDab = this.model.getDabData();
        let selectedCell = ticketDab.getSelectedCell();
        let symbol = this.symbolsByNumber.getValue(selectedCell.getNumber());
        if (this.model.isExpressGame() && !this.model.isStandardSpin() && !this.model.isPurchaseSpin()) {
            symbol.highlight(false);
        }
        else {
            symbol.dabSuccess();
        }
    }
    handleReelStop(stopIndex) {
        const ticketDab = this.model.getDabData();
        const reelDisplayData = this.model.getReelDisplayData();
        const autoHighlightCells = ticketDab.getAutoHighlightCells();
        const columnIndex = (typeof stopIndex === "number") ? stopIndex : reelDisplayData.getStopIndex();
        const displaySymbol = reelDisplayData.getDisplaySymbols()[columnIndex];
        const parsedReelNumber = parseInt(displaySymbol?.getId());
        for (let i = 0; i < autoHighlightCells.length; i++) {
            let cell = autoHighlightCells[i];
            if (cell.getNumber() === parsedReelNumber) {
                this.highlightCell(cell, false);
            }
        }
    }
    handleSlingo() {
        let winningPatterns = this.model.getSlingoPatterns();
        this.winningCells = [];
        let delay = this.uiProperties.slingoInitialDelay;
        for (let i = 0; i < winningPatterns.length; i++) {
            this.callAfter(delay, this.handleSlingoWin, i + 1);
            let pattern = winningPatterns[i];
            let rows = pattern.getRows();
            let columns = pattern.getColumns();
            for (let column = 0; column < columns; column++) {
                for (let row = 0; row < rows; row++) {
                    let cell = pattern.getCell(row, column);
                    if (cell.isActive()) {
                        this.winningCells.push(this.symbols.getValue(row, column));
                        this.callAfter(delay, this.handleSymbolWin);
                        delay += this.uiProperties.symbolWinInterval;
                    }
                }
            }
            delay += this.uiProperties.patternWinInterval;
        }
        this.callAfter(delay, this.handleSlingoComplete);
    }
    handleAvailableWinClear() {
        this.hideAvailableWin();
    }
    handleAvailableWin() {
        this.showAvailableWin();
    }
    handleSpinRequest() {
        if (this.model.isPurchaseSpin()) {
            this.hideAvailableWin();
            this.dispatchGame(SlingoEvent.AVAILABLE_WIN_END);
        }
    }
    handleCollectRequest() {
        this.hideAvailableWin();
        this.dispatchGame(SlingoEvent.AVAILABLE_WIN_END);
    }
    showAvailableWin() {
        let availableWin = this.model.getAvailableWin();
        let cells = availableWin.getCells();
        for (let i = 0; i < cells.length; i++) {
            this.showWin(cells[i]);
        }
    }
    hideAvailableWin() {
        this.hideWins();
    }
    handleSymbolWin() {
        if (this.winningCells.length > 0) {
            let symbol = this.winningCells.shift();
            symbol.win();
        }
    }
    handleSlingoWin(numberOfSlingos) {
        this.dispatchGame(SlingoEvent.getSlingoEvent(numberOfSlingos));
    }
    handleSlingoComplete() {
        if (this.model.isExpressGame()) {
            this.dispatchGame(SlingoEvent.SLINGO_ANIMATION);
            let winningPatterns = this.model.getSlingoPatterns();
            let delay = this.uiProperties.slingoInitialDelay;
            for (let i = 0; i < winningPatterns.length; i++) {
                this.callAfter(delay, this.handleSlingoWin, i + 1);
            }
        }
        else {
            if (this.factory.useFlowStateMachine?.()) {
                this.dispatchGame(SlingoEvent.SLINGO_TICKET_ANIMATION_COMPLETE);
            }
            else {
                this.dispatchGame(SlingoEvent.SLINGO_PAY_TABLE);
            }
        }
    }
    handleDabComplete() {
        this.disableSymbols();
    }
    handleDabHighlight() {
        let ticketDab = this.model.getDabData();
        this.highlightCells(ticketDab.getCellsToDab());
    }
    disableSymbols() {
        let symbols = this.symbolsByNumber.getValues();
        for (let i = 0; i < symbols.length; i++) {
            symbols[i].disable();
        }
    }
    showWin(cell) {
        let symbol = this.symbolsByNumber.getValue(cell.getNumber());
        symbol.showWin();
    }
    hideWins() {
        let symbols = this.symbolsByNumber.getValues();
        for (let i = 0; i < symbols.length; i++) {
            symbols[i].hideWin();
        }
    }
    // Big Win And Full House for now, look into if we can remove these later and just have a spine animation
    handleBigWinAnimationPhaseOne() {
        this.handleBigWinPattern();
        this.bigWinFlashTimer.start();
    }
    handleBigWinPattern() {
        let resultData = this.model.getResultData();
        let winningSymbols = this.getWinningSymbols(resultData.getCurrentPattern());
        for (let i = 0; i < winningSymbols.length; i++) {
            winningSymbols[i].bigWin();
        }
    }
    getWinningSymbols(pattern) {
        let winningSymbols = [];
        if (pattern) {
            let rows = pattern.getRows();
            let columns = pattern.getColumns();
            for (let row = 0; row < rows; row++) {
                for (let column = 0; column < columns; column++) {
                    let cell = pattern.getCell(row, column);
                    if (cell.isActive()) {
                        winningSymbols.push(this.symbols.getValue(row, column));
                    }
                }
            }
        }
        return winningSymbols;
    }
    handleResize(event) {
        if (this.symbolsPanel) {
            this.symbolsPanel.position.set(this.uiProperties.symbolPanelProperties.position.x, this.uiProperties.symbolPanelProperties.position.y);
            this.symbolsPanel.scale.set(this.uiProperties.symbolPanelProperties.scale.x, this.uiProperties.symbolPanelProperties.scale.y);
        }
    }
    handleRotate(event) {
        const data = event?.getCurrentTarget() || {};
        this.uiProperties = this.getCurrentProperties(data.forceOrientation);
        if (this.hasPortraitProperties) {
            this.layouts = [];
            this.applyProperties();
            if (this.symbolsPanel) {
                this.resizeView(this.symbolsPanel, this.uiProperties.symbolPanelProperties);
            }
        }
    }
    handleBigWinTimerUpdate() {
        let resultData = this.model.getResultData();
        if (resultData.hasNextPattern()) {
            resultData.nextPattern();
            this.handleBigWinPattern();
        }
        else {
            this.bigWinFlashTimer.stop();
            this.dispatchGame(SlingoEvent.BIG_WIN_ANIMATION_PHASE_TWO);
        }
    }
    handleFullHouseAnimationPhaseOne() {
        this.symbolsToAnimate = this.uiProperties.fullHousePattern;
        this.symbolAnimationIndex = 0;
        gsap.delayedCall(2, () => {
            this.dispatchGame(SlingoEvent.FULL_HOUSE_ANIMATION_PHASE_TWO);
        });
        this.animateSymbols();
    }
    handleDialogActive() {
        Logger.verbose("handleDialogActive");
        let result = this.model.getSpinResult();
        let reelSequence = result?.getReelSequence();
        let jokerIndexes = reelSequence?.getJokerIndexes() || [];
        let superJokerIndexes = reelSequence?.getSuperJokerIndexes() || [];
        if (jokerIndexes.length + superJokerIndexes.length === 0) {
            Logger.verbose("handleDialogActive - if statement");
            let symbols = this.symbolsByNumber.getValues();
            symbols.forEach(symbol => { symbol.pulseEnd(); });
        }
    }
    animateSymbols() {
        let symbols = this.symbolsToAnimate[this.symbolAnimationIndex];
        for (let i = 0; i < symbols.length; i++) {
            let point = symbols[i];
            let symbol = this.symbols.getValue(point.x, point.y);
            symbol.fullHouse();
        }
        let delay = this.uiProperties.fullHousePatternDuration / this.symbolsToAnimate.length;
        if (this.symbolAnimationIndex + 1 < this.symbolsToAnimate.length) {
            this.symbolAnimationIndex++;
            this.callAfter(delay, this.animateSymbols);
        }
    }
    handleFreeSpinAward() { }
    async dabAllSymbols() {
        const matchedNumbers = this.model.getGameProgress()?.getMatchedNumbers();
        matchedNumbers.forEach((number, index) => {
            this.symbolsByNumber.getValue(number).dabAfterHighlight();
        });
        // await this.pickHintedSymbol();
    }
}
//# sourceMappingURL=Ticket.js.map